import { useEffect, useMemo, useRef, useState } from "react";
import "./CriarModal.css";

const INICIAL = {
  atividade: "",
  unidade: "",
  controle_indicador: "",
  grupo: "",
  item_pdm: "",
};

const UP = (v) => (typeof v === "string" ? v.toUpperCase() : v ?? "");

const UNIDADES = [
  { value: "BIN\u00c1RIO", label: "BIN\u00c1RIO" },
  { value: "VAG", label: "VAG" },
  { value: "U", label: "U" },
  { value: "M", label: "M" },
  { value: "KM", label: "KM" },
];

const GRUPOS = [
  { value: "INFRA", label: "INFRA" },
  { value: "SUPER", label: "SUPER" },
  { value: "MECANIZA\u00c7\u00c3O", label: "MECANIZA\u00c7\u00c3O" },
  { value: "DETEC\u00c7\u00c3O", label: "DETEC\u00c7\u00c3O" },
  { value: "OUTROS", label: "OUTROS" },
  { value: "USINA", label: "USINA" },
];

const ITENS_PDM = [
  { value: "SIM", label: "SIM" },
  { value: "N\u00c3O", label: "N\u00c3O" },
];

function buildControleIndicador(grupo, atividade) {
  const g = UP(grupo || "").trim();
  const a = UP(atividade || "").trim();
  if (g && a) return `${g}-${a}`;
  if (g) return g;
  if (a) return a;
  return "";
}

function CriarModalDropdown({ label, name, value, options, onSelect, buttonRef, placeholder = "SELECIONE" }) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(220);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);
  const itemHeightRef = 44;

  const current = useMemo(() => options.find((o) => o.value === value) || null, [options, value]);

  useEffect(() => {
    function onDocDown(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) closeMenu();
    }
    document.addEventListener("mousedown", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    const onWindowChange = () => {
      updatePlacement();
    };

    window.addEventListener("resize", onWindowChange);
    window.addEventListener("scroll", onWindowChange, true);
    return () => {
      window.removeEventListener("resize", onWindowChange);
      window.removeEventListener("scroll", onWindowChange, true);
    };
  }, [open, options.length]);

  function updatePlacement() {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const modal = wrapRef.current.closest(".crmd-box");
    const modalRect = modal?.getBoundingClientRect();

    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const safePad = 12;

    const viewportBelow = Math.max(0, viewportH - rect.bottom - safePad);
    const viewportAbove = Math.max(0, rect.top - safePad);
    const modalBelow = modalRect ? Math.max(0, modalRect.bottom - rect.bottom - safePad) : Number.POSITIVE_INFINITY;
    const modalAbove = modalRect ? Math.max(0, rect.top - modalRect.top - safePad) : Number.POSITIVE_INFINITY;

    const spaceBelow = Math.max(0, Math.min(viewportBelow, modalBelow));
    const spaceAbove = Math.max(0, Math.min(viewportAbove, modalAbove));

    // Altura alvo da lista; se nao couber, reduz e habilita scroll interno.
    const target = Math.min(320, options.length * itemHeightRef + 12);
    const shouldOpenUp = spaceBelow < 190 && spaceAbove > spaceBelow;
    const available = shouldOpenUp ? spaceAbove : spaceBelow;
    const dynamicMax = Math.max(96, Math.min(target, available - 6));

    setOpenUp(shouldOpenUp);
    setMenuMaxHeight(dynamicMax);
  }

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setRenderMenu(true);
    requestAnimationFrame(() => {
      updatePlacement();
      setOpen(true);
    });
  };

  const closeMenu = () => {
    setOpen(false);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setRenderMenu(false), 240);
  };

  const toggle = () => {
    if (open) closeMenu();
    else openMenu();
  };

  return (
    <div ref={wrapRef} className={`crmd-field crmd-field-select ${open ? "is-open" : ""} ${openUp ? "is-open-up" : ""}`}>
      <label className="crmd-label">{label}</label>
      <div className="crmd-pick-anchor">
        <button
          ref={buttonRef}
          type="button"
          className="crmd-pick-btn"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={toggle}
        >
          <span className={`crmd-pick-val ${current ? "" : "is-placeholder"}`}>
            {current ? current.label : placeholder}
          </span>
        </button>

        {renderMenu && (
          <div
            className={`crmd-pick-menu ${open ? "is-open" : "is-closing"}`}
            role="listbox"
            aria-label={label}
            style={{ maxHeight: `${menuMaxHeight}px` }}
          >
            {options.map((opt) => {
              return (
                <button
                  key={opt.value}
                  type="button"
                  className="crmd-pick-opt"
                  onClick={() => {
                    onSelect(name, opt.value);
                    closeMenu();
                  }}
                  role="option"
                  aria-selected={opt.value === value}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CriarModal({ open, onClose, onSalvar }) {
  const [form, setForm] = useState(INICIAL);

  const firstRef = useRef(null);
  const unidadeRef = useRef(null);
  const itemPdmRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm({ ...INICIAL });

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => firstRef.current?.focus(), 0);

    // Mantem ESC bloqueado neste popup
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    document.addEventListener("keydown", onKey, true);

    return () => {
      clearTimeout(t);
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  if (!open) return null;

  const setField = (name, value) => {
    setForm((prev) => {
      const up = UP(value || "");
      const next = { ...prev, [name]: up };

      if (name === "grupo" || name === "atividade") {
        next.controle_indicador = buildControleIndicador(
          name === "grupo" ? value : prev.grupo,
          name === "atividade" ? value : prev.atividade
        );
      }

      return next;
    });
  };

  const handleChange = (e) => {
    setField(e.target.name, e.target.value);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!form.atividade.trim()) {
      firstRef.current?.focus();
      return;
    }
    if (!form.unidade) {
      unidadeRef.current?.focus();
      return;
    }
    if (!form.item_pdm) {
      itemPdmRef.current?.focus();
      return;
    }

    const payload = {
      ...form,
      atividade: UP(form.atividade),
      unidade: UP(form.unidade),
      grupo: UP(form.grupo),
      item_pdm: UP(form.item_pdm),
      controle_indicador: buildControleIndicador(form.grupo, form.atividade),
    };

    await onSalvar?.(payload);
  };

  const handleClear = () => setForm({ ...INICIAL });

  return (
    <div className="crmd-bg" data-crmd>
      <div className="crmd-box" role="dialog" aria-modal="true" aria-label="Criar atividade">
        <header className="crmd-head">
          <h3 className="crmd-title">Criar atividade</h3>
          <button className="crmd-x" onClick={onClose} type="button" aria-label="Fechar" title="Fechar">
            x
          </button>
        </header>

        <div className="crmd-body">
          <form className="crmd-grid" onSubmit={handleSubmit}>
            <div className="crmd-field">
              <label className="crmd-label">Atividade</label>
              <input
                ref={firstRef}
                name="atividade"
                value={form.atividade}
                onChange={handleChange}
                placeholder="EX.: SOCARIA"
                className="crmd-input"
              />
            </div>

            <CriarModalDropdown
              label="Unidade"
              name="unidade"
              value={form.unidade}
              options={UNIDADES}
              onSelect={setField}
              buttonRef={unidadeRef}
            />

            <CriarModalDropdown
              label="Grupo"
              name="grupo"
              value={form.grupo}
              options={GRUPOS}
              onSelect={setField}
            />

            <CriarModalDropdown
              label="Item PDM"
              name="item_pdm"
              value={form.item_pdm}
              options={ITENS_PDM}
              onSelect={setField}
              buttonRef={itemPdmRef}
            />
          </form>
        </div>

        <footer className="crmd-foot">
          {/* Mantido: Cancelar nao fecha, apenas limpa */}
          <button className="crmd-btn" type="button" onClick={handleClear}>
            Cancelar
          </button>
          <button className="crmd-btn crmd-btn-ok" type="button" onClick={handleSubmit}>
            Cadastrar
          </button>
        </footer>
      </div>
    </div>
  );
}
